import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowRight, 
  FileText, 
  CreditCard, 
  MessageCircle, 
  BarChart3,
  CheckCircle,
  Send,
  DollarSign,
  Calendar,
  Bell,
  Users,
  TrendingUp,
  Shield,
  Zap,
  Clock,
  Play,
  Pause,
  ChevronRight,
  ChevronLeft,
  Mail,
  Timer,
  RefreshCw,
  Sparkles,
  Eye,
  Download,
  Star,
  XCircle
} from 'lucide-react';

const FeaturesPage = () => {
  const [activeInvoiceStep, setActiveInvoiceStep] = useState(0);
  const [activePaymentStep, setActivePaymentStep] = useState(0);
  const [activeChatStep, setActiveChatStep] = useState(0);
  const [activeAnalyticsStep, setActiveAnalyticsStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [autoChatCycle, setAutoChatCycle] = useState(true);
  const [autoInvoiceCycle, setAutoInvoiceCycle] = useState(true);
  const [autoAnalyticsCycle, setAutoAnalyticsCycle] = useState(true);

  const invoiceSteps = [
    {
      title: "Wizard Invoice Creation",
      description: "Step-by-step wizard with auto-population and validation",
      icon: FileText,
      details: "Multi-step wizard interface with contact lookup and line item management",
      visual: "invoice-creation"
    },
    {
      title: "Customer Approval Portal",
      description: "Secure guest portal for instant customer approval",
      icon: Send,
      details: "Token-based secure access with early payment incentives",
      visual: "invoice-sending"
    },
    {
      title: "Smart Auto-Approval",
      description: "7-day rule with intelligent payment authorization",
      icon: CheckCircle,
      details: "Automatic approval for customers with verified payment methods",
      visual: "invoice-approval"
    },
    {
      title: "Stripe Connect Payment",
      description: "Seamless payment collection via Stripe Connect",
      icon: CreditCard,
      details: "Automatic payment processing with real-time reconciliation",
      visual: "payment-collection"
    }
  ];

  const paymentSteps = [
    {
      title: "Customer Authorization",
      description: "One-click approval with secure bank details",
      icon: CheckCircle,
      details: "PCI-compliant payment method collection",
      visual: "payment-approval"
    },
    {
      title: "Payment Scheduling",
      description: "Automatically queued for due date collection",
      icon: Calendar,
      details: "Smart scheduling prevents early payment issues",
      visual: "payment-scheduling"
    },
    {
      title: "Auto Collection",
      description: "Payment processed automatically on due date",
      icon: CreditCard,
      details: "No manual intervention required",
      visual: "payment-processing"
    },
    {
      title: "Instant Notification",
      description: "Real-time payment confirmation",
      icon: Bell,
      details: "Get notified immediately when payment succeeds",
      visual: "payment-notification"
    }
  ];

  const chatSteps = [
    {
      title: "Invoice-Specific Discussions",
      description: "Chat directly about specific invoices",
      icon: MessageCircle,
      details: "All conversations linked to invoice context",
      visual: "chat-invoice"
    },
    {
      title: "File Sharing",
      description: "Share contracts, receipts, and documents",
      icon: FileText,
      details: "Secure file sharing within conversations",
      visual: "chat-files"
    },
    {
      title: "Real-Time Notifications",
      description: "Never miss important discussions",
      icon: Bell,
      details: "Instant alerts for all activity",
      visual: "chat-notifications"
    },
    {
      title: "Resolution Tracking",
      description: "Complete audit trail of all discussions",
      icon: BarChart3,
      details: "Track conversation history and outcomes",
      visual: "chat-tracking"
    }
  ];

  const analyticsSteps = [
    {
      title: "Live Dashboard",
      description: "Real-time business performance metrics",
      icon: BarChart3,
      details: "See cash flow, outstanding invoices, and payment trends",
      visual: "analytics-dashboard"
    },
    {
      title: "Cashflow Forecast",
      description: "Projected incoming payments from approved invoices",
      icon: TrendingUp,
      details: "Rolling forecast showing payments over next 1, 7, and 14 days",
      visual: "analytics-forecast"
    },
    {
      title: "Payment Tracking",
      description: "Monitor invoice statuses and payment patterns",
      icon: Users,
      details: "Track sent, received, paid, overdue, and rejected invoices",
      visual: "analytics-customers"
    },
    {
      title: "Early Payment Incentives",
      description: "Encourage faster payments with discount offers",
      icon: Sparkles,
      details: "Offer early payment discounts to improve cash flow",
      visual: "analytics-recommendations"
    }
  ];

  // Auto-cycle chat cards with smooth transitions
  useEffect(() => {
    if (autoChatCycle) {
      const interval = setInterval(() => {
        setActiveChatStep(prev => (prev + 1) % 4);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [autoChatCycle]);

  // Auto-cycle invoice steps with smooth transitions
  useEffect(() => {
    if (autoInvoiceCycle) {
      const interval = setInterval(() => {
        setActiveInvoiceStep(prev => (prev + 1) % 4);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [autoInvoiceCycle]);

  // Auto-cycle analytics steps with smooth transitions
  useEffect(() => {
    if (autoAnalyticsCycle) {
      const interval = setInterval(() => {
        setActiveAnalyticsStep(prev => (prev + 1) % 4);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [autoAnalyticsCycle]);

  // Global play/pause functionality
  useEffect(() => {
    if (isPlaying) {
      setAutoInvoiceCycle(true);
      setAutoChatCycle(true);
      setAutoAnalyticsCycle(true);
    } else {
      setAutoInvoiceCycle(false);
      setAutoChatCycle(false);
      setAutoAnalyticsCycle(false);
    }
  }, [isPlaying]);

  return (
    <>
      <Helmet>
        <title>Features - Automated Credit Control Management | Receevit®</title>
        <meta name="description" content="Smart invoice automation, automated reminders, Stripe integration, real-time chat, and advanced analytics. Discover how Receevit® transforms B2B credit control." />
        <link rel="canonical" href="https://www.receevit.com/features" />
        <meta property="og:title" content="Features - Automated Credit Control Management | Receevit®" />
        <meta property="og:description" content="Smart invoice automation, automated reminders, Stripe integration, real-time chat, and advanced analytics. Discover how Receevit® transforms B2B credit control." />
        <meta property="og:url" content="https://www.receevit.com/features" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.receevit.com/og-image.png" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Features - Automated Credit Control Management | Receevit®",
            "url": "https://www.receevit.com/features",
            "description": "Smart invoice automation, automated reminders, Stripe integration, real-time chat, and advanced analytics. Discover how Receevit® transforms B2B credit control.",
            "publisher": {
              "@type": "Organization",
              "name": "Receevit®",
              "url": "https://www.receevit.com/"
            }
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://www.receevit.com/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Features",
                "item": "https://www.receevit.com/features"
              }
            ]
          })}
        </script>
      </Helmet>
      
      <div className="pt-16">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-[#1F11ED] py-20">

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-6">
            See How Receevit®{' '}
            <span className="bg-gradient-to-r from-blue-300 via-white to-blue-300 bg-clip-text text-transparent">
              Actually Works
            </span>
          </h1>
          
          <p className="text-xl md:text-2xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Interactive demos showing exactly how your business will get paid faster
          </p>

          {/* Global Play/Pause Controls */}
          <div className="flex justify-center items-center gap-4">
            <button
              onClick={() => setIsPlaying(!isPlaying)}
              className="flex items-center gap-2 bg-white/20 backdrop-blur-sm text-white px-6 py-3 rounded-full font-semibold hover:bg-white/30 transition-all duration-300"
            >
              {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
              {isPlaying ? 'Pause All Demos' : 'Play All Demos'}
            </button>
            {isPlaying && (
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-green-300 text-sm">All demos playing</span>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Smart Invoice Flow Section */}
      <section className="relative py-16 bg-[#1F11ED] overflow-hidden">
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
              Smart Invoice Flow
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto mb-6">
              From creation to payment in 4 simple steps
            </p>
            <div className="flex justify-center items-center gap-4">
              <button
                onClick={() => {
                  setAutoInvoiceCycle(!autoInvoiceCycle);
                  setIsPlaying(false);
                }}
                className="flex items-center gap-2 bg-white/20 backdrop-blur-sm text-white px-6 py-3 rounded-full font-semibold hover:bg-white/30 transition-all duration-300"
              >
                {autoInvoiceCycle ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                {autoInvoiceCycle ? 'Pause Demo' : 'Play Demo'}
              </button>
              {autoInvoiceCycle && (
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                  <span className="text-green-300 text-sm">Auto-playing</span>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Steps Navigation */}
            <div className="space-y-4">
              {invoiceSteps.map((step, index) => (
                <motion.button
                  key={index}
                  onClick={() => {
                    setActiveInvoiceStep(index);
                    setAutoInvoiceCycle(false);
                  }}
                  className={`w-full text-left p-6 rounded-2xl transition-all duration-300 ${
                    activeInvoiceStep === index
                      ? 'bg-white text-[#1F11ED] shadow-2xl'
                      : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                  animate={{ scale: 1, y: 0 }}
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                >
                  <div className="flex items-center gap-4">
          <motion.div
                      className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        activeInvoiceStep === index ? 'bg-[#1F11ED] text-white' : 'bg-white/20 text-white'
                      }`}
                      animate={{ scale: 1, rotate: 0 }}
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      transition={{ duration: 0.2, ease: "easeOut" }}
                    >
                      <step.icon className="w-6 h-6" />
                    </motion.div>
                    <div>
                      <h3 className="text-lg font-bold mb-1">{step.title}</h3>
                      <p className="text-sm opacity-80">{step.description}</p>
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>

            {/* Visual Demo */}
            <motion.div 
              className="bg-white/10 backdrop-blur-sm rounded-3xl p-8"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            >
              <div className="text-center">
                <motion.div 
                  className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-6"
                  animate={{ scale: 1, rotate: 0 }}
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                >
                  {React.createElement(invoiceSteps[activeInvoiceStep].icon, { className: "w-10 h-10 text-[#1F11ED]" })}
                </motion.div>
                <h3 className="text-2xl font-bold text-white mb-4">
                  {invoiceSteps[activeInvoiceStep].title}
                </h3>
                <p className="text-blue-100 mb-6">
                  {invoiceSteps[activeInvoiceStep].details}
                </p>
                
                {/* Step-specific visual */}
                <AnimatePresence mode="wait">
                  {activeInvoiceStep === 0 && (
                    <motion.div
                      key="step-0"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                    >
                  <div className="bg-white rounded-2xl p-6 max-w-md mx-auto">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-lg font-bold text-gray-900">Invoice Creation Wizard</h4>
                      <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">Step 2 of 4</span>
                    </div>
                    <div className="space-y-3 text-left">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Recipient:</span>
                        <span className="font-medium">Customer Ltd</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Line Items:</span>
                        <span className="font-medium">3 items</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Early Payment:</span>
                        <span className="font-medium text-green-600">5% discount</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Amount:</span>
                        <span className="font-bold text-lg text-[#1F11ED]">£2,500.00</span>
                      </div>
                    </div>
                    <div className="mt-4 flex gap-2">
                      <button className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-lg font-medium">
                        Previous
                      </button>
                      <button className="flex-1 bg-[#1F11ED] text-white py-2 px-4 rounded-lg font-medium">
                        Next Step
                      </button>
                    </div>
                  </div>
                    </motion.div>
                  )}

                  {activeInvoiceStep === 1 && (
                    <motion.div
                      key="step-1"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                    >
                  <div className="max-w-md mx-auto">
                    <div className="bg-green-100 border border-green-200 rounded-lg p-4 mb-4">
                      <div className="flex items-center">
                        <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                        <span className="text-green-800 font-medium">Invoice sent successfully!</span>
                      </div>
                    </div>
                    <div className="bg-white rounded-2xl p-6">
                      <div className="text-center">
                        <Mail className="w-12 h-12 text-[#1F11ED] mx-auto mb-4" />
                        <h4 className="text-lg font-bold text-gray-900 mb-2">Guest Invoice Portal</h4>
                        <p className="text-sm text-gray-600 mb-4">Secure token-based access with early payment incentives</p>
                        <div className="bg-blue-50 rounded-lg p-3">
                          <p className="text-sm text-blue-800 font-medium">🎉 Early Payment Incentive Available!</p>
                          <p className="text-xs text-blue-600 mt-1">5% discount if paid within 7 days</p>
                        </div>
                      </div>
                    </div>
                  </div>
                    </motion.div>
                  )}

                  {activeInvoiceStep === 2 && (
                    <motion.div
                      key="step-2"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                    >
                  <div className="max-w-md mx-auto">
                    <div className="bg-blue-100 border border-blue-200 rounded-lg p-4 mb-4">
                      <div className="flex items-center justify-between">
                        <span className="text-blue-800 font-medium">Auto-approval in progress</span>
                        <Timer className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="mt-2">
                        <div className="w-full bg-blue-200 rounded-full h-2">
                          <div className="bg-blue-600 h-2 rounded-full" style={{width: '75%'}}></div>
                        </div>
                        <p className="text-xs text-blue-600 mt-1">5 days remaining</p>
                      </div>
                    </div>
                    <div className="bg-white rounded-2xl p-6">
                      <div className="text-center">
                        <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-4" />
                        <h4 className="text-lg font-bold text-gray-900 mb-2">Invoice will auto-approve</h4>
                        <p className="text-sm text-gray-600">If no action taken by customer</p>
                      </div>
                    </div>
                  </div>
                    </motion.div>
                  )}

                  {activeInvoiceStep === 3 && (
                    <motion.div
                      key="step-3"
                      initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                    >
                  <div className="max-w-md mx-auto">
                    <div className="bg-green-100 border border-green-200 rounded-lg p-4 mb-4">
                      <div className="flex items-center">
                        <CreditCard className="w-5 h-5 text-green-600 mr-2" />
                        <span className="text-green-800 font-medium">Payment collected!</span>
                      </div>
                    </div>
                    <div className="bg-white rounded-2xl p-6">
                      <div className="text-center">
                        <div className="text-3xl font-bold text-green-600 mb-2">£2,500.00</div>
                        <h4 className="text-lg font-bold text-gray-900 mb-2">Successfully transferred</h4>
                        <p className="text-sm text-gray-600">No manual intervention needed</p>
                      </div>
                    </div>
                  </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Automated Payments Section - Timeline Design */}
      <section className="relative py-16 bg-[#1F11ED] overflow-hidden">
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
              Automated Payments
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Watch how payments flow automatically from authorization to your bank account
            </p>
          </div>

          {/* Interactive Timeline */}
          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-white/20 rounded-full"></div>
            
            {/* Timeline Steps */}
            <div className="space-y-16">
              {paymentSteps.map((step, index) => (
                <div
                  key={index}
                  className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}
                >
                  {/* Timeline Dot */}
                  <div className="relative z-10">
                    <button
                      onClick={() => setActivePaymentStep(index)}
                      className={`w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300 ${
                        activePaymentStep === index
                          ? 'bg-white text-[#1F11ED] shadow-2xl scale-110'
                          : 'bg-white/20 text-white hover:bg-white/30'
                      }`}
                    >
                      {React.createElement(step.icon, { className: "w-8 h-8" })}
                    </button>
                  </div>

                  {/* Content Card */}
                  <div className={`flex-1 ${index % 2 === 0 ? 'ml-8' : 'mr-8'}`}>
                    <div
                      className={`bg-white/10 backdrop-blur-sm rounded-2xl p-6 cursor-pointer transition-all duration-300 ${
                        activePaymentStep === index ? 'bg-white/20 shadow-2xl' : 'hover:bg-white/15'
                      }`}
                      onClick={() => setActivePaymentStep(index)}
                    >
                      <div className="flex items-start gap-4">
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-white mb-2">{step.title}</h3>
                          <p className="text-blue-100 mb-4">{step.description}</p>
                          
                          {/* Step-specific visual */}
                          {activePaymentStep === index && (
                            <div className="mt-4">
                              {index === 0 && (
                                <div className="bg-white rounded-xl p-4">
                                  <div className="text-center mb-4">
                                    <h4 className="text-lg font-bold text-gray-900">Payment Authorization</h4>
                                    <p className="text-sm text-gray-600">Customer enters payment details</p>
                                  </div>
                                  <div className="space-y-3">
                                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                      <span className="text-gray-600">Amount:</span>
                                      <span className="font-bold text-[#1F11ED]">£2,500.00</span>
                                    </div>
                                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                      <span className="text-gray-600">Due Date:</span>
                                      <span className="font-medium">Dec 15, 2024</span>
                                    </div>
                                    <button className="w-full bg-green-600 text-white py-3 px-4 rounded-lg font-medium">
                                      Authorize Payment
                                    </button>
                                  </div>
                                </div>
                              )}

                              {index === 1 && (
                                <div className="bg-white rounded-xl p-4">
                                  <div className="flex items-center mb-4">
                                    <Calendar className="w-6 h-6 text-blue-600 mr-2" />
                                    <span className="text-blue-800 font-medium">Payment scheduled</span>
                                  </div>
                                  <div className="text-center">
                                    <div className="text-2xl font-bold text-blue-600 mb-2">Dec 15, 2024</div>
                                    <h4 className="text-lg font-bold text-gray-900 mb-2">Payment will be collected automatically</h4>
                                    <p className="text-sm text-gray-600">No action required from you</p>
                                  </div>
                                </div>
                              )}

                              {index === 2 && (
                                <div className="bg-white rounded-xl p-4">
                                  <div className="flex items-center mb-4">
                                    <RefreshCw className="w-6 h-6 text-yellow-600 mr-2 animate-spin" />
                                    <span className="text-yellow-800 font-medium">Processing payment...</span>
                                  </div>
                                  <div className="text-center">
                                    <CreditCard className="w-12 h-12 text-[#1F11ED] mx-auto mb-4" />
                                    <h4 className="text-lg font-bold text-gray-900 mb-2">Collecting payment from customer</h4>
                                    <p className="text-sm text-gray-600">This happens automatically</p>
                                  </div>
                                </div>
                              )}

                              {index === 3 && (
                                <div className="bg-white rounded-xl p-4">
                                  <div className="flex items-center mb-4">
                                    <CheckCircle className="w-6 h-6 text-green-600 mr-2" />
                                    <span className="text-green-800 font-medium">Payment successful!</span>
                                  </div>
                                  <div className="text-center">
                                    <div className="text-3xl font-bold text-green-600 mb-2">£2,500.00</div>
                                    <h4 className="text-lg font-bold text-gray-900 mb-2">Transferred to your account</h4>
                                    <p className="text-sm text-gray-600">You received instant notification</p>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Real-Time Communication Section - Card Grid Design */}
      <section className="relative py-16 bg-[#1F11ED] overflow-hidden">
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
              Real-Time Communication
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto mb-6">
              Never miss a payment discussion with built-in chat and collaboration tools
            </p>
            <div className="flex justify-center items-center gap-4">
              <button
                onClick={() => {
                  setAutoChatCycle(!autoChatCycle);
                  setIsPlaying(false);
                }}
                className="flex items-center gap-2 bg-white/20 backdrop-blur-sm text-white px-6 py-3 rounded-full font-semibold hover:bg-white/30 transition-all duration-300"
              >
                {autoChatCycle ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                {autoChatCycle ? 'Pause Demo' : 'Play Demo'}
              </button>
              {autoChatCycle && (
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                  <span className="text-green-300 text-sm">Auto-playing</span>
                </div>
              )}
            </div>
          </div>

          {/* Interactive Card Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {chatSteps.map((step, index) => (
              <motion.div
                key={index}
                onClick={() => {
                  setActiveChatStep(index);
                  setAutoChatCycle(false);
                }}
                className={`bg-white/10 backdrop-blur-sm rounded-2xl p-6 cursor-pointer transition-all duration-300 ${
                  activeChatStep === index ? 'bg-white/20 shadow-2xl' : 'hover:bg-white/15'
                }`}
                animate={{ scale: 1, y: 0 }}
                whileHover={{ scale: 1.05, y: -5 }}
                whileTap={{ scale: 0.95 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
              >
                <div className="text-center">
                  <motion.div 
                    className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                      activeChatStep === index ? 'bg-white text-[#1F11ED]' : 'bg-white/20 text-white'
                    }`}
                    animate={{ scale: 1, rotate: 0 }}
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                  >
                    {React.createElement(step.icon, { className: "w-8 h-8" })}
                  </motion.div>
                  <h3 className="text-lg font-bold text-white mb-2">{step.title}</h3>
                  <p className="text-blue-100 text-sm mb-4">{step.description}</p>
                  
                  {/* Step-specific mini visual */}
                  <AnimatePresence>
                    {activeChatStep === index && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                      >
                    <div className="bg-white rounded-lg p-3 text-left">
                      {index === 0 && (
                        <div className="space-y-2">
                          <div className="bg-blue-50 p-2 rounded text-xs">
                            <p className="text-gray-800">Hi, I have a question...</p>
                            <p className="text-gray-500 text-xs">Customer • 2 min ago</p>
                          </div>
                          <div className="bg-gray-50 p-2 rounded text-xs">
                            <p className="text-gray-800">Sure, what would you like to know?</p>
                            <p className="text-gray-500 text-xs">You • Just now</p>
                          </div>
                        </div>
                      )}
                      
                      {index === 1 && (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-green-600" />
                            <span className="text-xs text-gray-800">contract.pdf</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-[#1F11ED]" />
                            <span className="text-xs text-gray-800">receipt.pdf</span>
                          </div>
                        </div>
                      )}
                      
                      {index === 2 && (
                        <div className="flex items-center gap-2">
                          <Bell className="w-4 h-4 text-yellow-600" />
                          <span className="text-xs text-gray-800">New message received</span>
                        </div>
                      )}
                      
                      {index === 3 && (
                        <div className="space-y-1">
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-600">Invoice #INV-001</span>
                            <span className="text-green-600">Resolved</span>
                          </div>
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-600">Invoice #INV-002</span>
                            <span className="text-yellow-600">In Progress</span>
                          </div>
                        </div>
                      )}
                    </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Business Analytics Section - Dashboard Design */}
      <section className="relative py-16 bg-[#1F11ED] overflow-hidden">
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
              Business Analytics
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto mb-6">
              Make smarter business decisions with real-time insights and cash flow forecasting
            </p>
            <div className="flex justify-center items-center gap-4">
              <button
                onClick={() => {
                  setAutoAnalyticsCycle(!autoAnalyticsCycle);
                  setIsPlaying(false);
                }}
                className="flex items-center gap-2 bg-white/20 backdrop-blur-sm text-white px-6 py-3 rounded-full font-semibold hover:bg-white/30 transition-all duration-300"
              >
                {autoAnalyticsCycle ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                {autoAnalyticsCycle ? 'Pause Demo' : 'Play Demo'}
              </button>
              {autoAnalyticsCycle && (
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                  <span className="text-green-300 text-sm">Auto-playing</span>
                </div>
              )}
            </div>
              </div>

          {/* Interactive Dashboard */}
          <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8">
            {/* Dashboard Tabs */}
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              {analyticsSteps.map((step, index) => (
                <motion.button
                  key={index}
                  onClick={() => {
                    setActiveAnalyticsStep(index);
                    setAutoAnalyticsCycle(false);
                  }}
                  className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                    activeAnalyticsStep === index
                      ? 'bg-white text-[#1F11ED] shadow-lg'
                      : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                  animate={{ scale: 1, y: 0 }}
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                >
                  {step.title}
                </motion.button>
              ))}
            </div>
            
            {/* Dashboard Content */}
            <motion.div 
              className="bg-white rounded-2xl p-8"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            >
              <div className="text-center mb-6">
                <motion.div 
                  className="w-16 h-16 bg-[#1F11ED] rounded-full flex items-center justify-center mx-auto mb-4"
                  animate={{ scale: 1, rotate: 0 }}
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                >
                  {React.createElement(analyticsSteps[activeAnalyticsStep].icon, { className: "w-8 h-8 text-white" })}
                </motion.div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  {analyticsSteps[activeAnalyticsStep].title}
                </h3>
                <p className="text-gray-600">
                  {analyticsSteps[activeAnalyticsStep].details}
                </p>
              </div>

              {/* Dashboard Visual */}
              <AnimatePresence mode="wait">
                {activeAnalyticsStep === 0 && (
                  <motion.div
                    key="analytics-0"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                  >
                <div className="space-y-6">
                  {/* Modern Horizontal Split Cards */}
                  <div className="group p-6 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 flex items-center gap-4" style={{backgroundColor: '#f9fafb'}}>
                    <div className="flex-shrink-0">
                      <div className="w-14 h-14 bg-emerald-50 rounded-xl flex items-center justify-center group-hover:bg-emerald-100 transition-colors">
                        <TrendingUp className="w-7 h-7 text-emerald-600" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Total Value Collected</p>
                      <p className="text-xs text-gray-400">monthly</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-gray-900 whitespace-nowrap">£45,230</p>
                    </div>
                  </div>
                  
                  <div className="group p-6 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 flex items-center gap-4" style={{backgroundColor: '#f9fafb'}}>
                    <div className="flex-shrink-0">
                      <div className="w-14 h-14 bg-blue-50 rounded-xl flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                        <Calendar className="w-7 h-7 text-blue-600" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Next Payment Due</p>
                      <p className="text-xs text-gray-400">Dec 15, 2024</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-gray-900 whitespace-nowrap">£12,450</p>
                    </div>
                  </div>
                  
                  <div className="group p-6 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 flex items-center gap-4" style={{backgroundColor: '#f9fafb'}}>
                    <div className="flex-shrink-0">
                      <div className="w-14 h-14 bg-red-50 rounded-xl flex items-center justify-center group-hover:bg-red-100 transition-colors">
                        <XCircle className="w-7 h-7 text-red-600" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Rejected Invoices</p>
                      <p className="text-xs text-gray-400">This month</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-gray-900 whitespace-nowrap">3</p>
                    </div>
                  </div>
                  
                  <div className="group p-6 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 flex items-center gap-4" style={{backgroundColor: '#f9fafb'}}>
                    <div className="flex-shrink-0">
                      <div className="w-14 h-14 bg-green-50 rounded-xl flex items-center justify-center group-hover:bg-green-100 transition-colors">
                        <CheckCircle className="w-7 h-7 text-green-600" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Approved Invoices</p>
                      <p className="text-xs text-gray-400">This month</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-gray-900 whitespace-nowrap">23</p>
                    </div>
                  </div>
                </div>
                  </motion.div>
                )}

                {activeAnalyticsStep === 1 && (
                  <motion.div
                    key="analytics-1"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                  >
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-6">
                    <h4 className="text-lg font-bold text-gray-900 mb-4">Rolling Cash Flow Forecast</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-600 mb-1">+£8,500</div>
                        <div className="text-sm text-gray-600">Next 1 day</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-blue-600 mb-1">+£18,200</div>
                        <div className="text-sm text-gray-600">Next 7 days</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-purple-600 mb-1">+£32,700</div>
                        <div className="text-sm text-gray-600">Next 14 days</div>
                      </div>
                    </div>
                  </div>
                </div>
                  </motion.div>
                )}

                {activeAnalyticsStep === 2 && (
                  <motion.div
                    key="analytics-2"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                  >
                <div className="space-y-6">
                  <h4 className="text-lg font-bold text-gray-900">Top Performing Customers</h4>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <span className="text-blue-600 font-bold">AC</span>
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">Acme Corp</div>
                          <div className="text-sm text-gray-600">£15,000 total paid</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-green-600">98%</div>
                        <div className="text-sm text-gray-600">Payment Rate</div>
                      </div>
                    </div>
                  </div>
                </div>
                  </motion.div>
                )}

                {activeAnalyticsStep === 3 && (
                  <motion.div
                    key="analytics-3"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                  >
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-6">
                    <div className="flex items-center mb-4">
                      <Sparkles className="w-6 h-6 text-green-600 mr-2" />
                      <h4 className="text-lg font-bold text-gray-900">Payment Incentive Strategies</h4>
                    </div>
                    <div className="space-y-4">
                      <div className="bg-white rounded-lg p-4 border border-green-200">
                        <div className="flex items-start gap-3">
                          <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                          <div>
                            <div className="font-medium text-gray-900 mb-1">2% discount for payment within 10 days</div>
                            <div className="text-sm text-gray-600">Encourage faster payments with early bird discounts</div>
                          </div>
                        </div>
                      </div>
                      <div className="bg-white rounded-lg p-4 border border-blue-200">
                        <div className="flex items-start gap-3">
                          <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                          <div>
                            <div className="font-medium text-gray-900 mb-1">Loyalty rewards for consistent payers</div>
                            <div className="text-sm text-gray-600">Reward customers who pay on time consistently</div>
                          </div>
                    </div>
                      </div>
                    </div>
                  </div>
                </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Additional Platform Features */}
      <section className="py-20 bg-[#1F11ED]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Complete Business Management Suite
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Beyond invoicing, Receevit® provides all the tools you need to manage your business relationships and operations.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Contact Management */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <div className="text-white mb-4">
                <Users className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Contact Management</h3>
              <p className="text-blue-100 leading-relaxed mb-4">
                Organise and manage all your business contacts with detailed profiles and communication history.
              </p>
              <ul className="text-sm text-blue-100 space-y-1">
                <li>• Contact profiles with full details</li>
                <li>• Communication history tracking</li>
                <li>• Auto-populate invoice details</li>
              </ul>
            </div>

            {/* Task Management */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <div className="text-white mb-4">
                <CheckCircle className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Smart To-Do Lists</h3>
              <p className="text-blue-100 leading-relaxed mb-4">
                Stay organised with intelligent task management and automated reminders.
              </p>
              <ul className="text-sm text-blue-100 space-y-1">
                <li>• Automated daily tasks</li>
                <li>• Invoice follow-up reminders</li>
                <li>• Team collaboration</li>
              </ul>
            </div>

            {/* Settings & Security */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <div className="text-white mb-4">
                <Shield className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Advanced Settings</h3>
              <p className="text-blue-100 leading-relaxed mb-4">
                Comprehensive settings and security features for complete control.
              </p>
              <ul className="text-sm text-blue-100 space-y-1">
                <li>• User management & permissions</li>
                <li>• Notification preferences</li>
                <li>• Security & login history</li>
              </ul>
            </div>

            {/* Payment Integration */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <div className="text-white mb-4">
                <CreditCard className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Stripe Connect</h3>
              <p className="text-blue-100 leading-relaxed mb-4">
                Seamless payment processing with industry-leading security and reliability.
              </p>
              <ul className="text-sm text-blue-100 space-y-1">
                <li>• Secure payment collection</li>
                <li>• Automatic reconciliation</li>
                <li>• Real-time payment tracking</li>
              </ul>
            </div>

            {/* Global Search */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <div className="text-white mb-4">
                <Eye className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Global Search</h3>
              <p className="text-blue-100 leading-relaxed mb-4">
                Find anything instantly with powerful search across all your data.
              </p>
              <ul className="text-sm text-blue-100 space-y-1">
                <li>• Search contacts, invoices, tasks</li>
                <li>• Quick navigation to results</li>
                <li>• Smart filtering & highlighting</li>
              </ul>
            </div>

            {/* Early Payment Incentives */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <div className="text-white mb-4">
                <Sparkles className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Early Payment Incentives</h3>
              <p className="text-blue-100 leading-relaxed mb-4">
                Encourage faster payments with automated discount offers and incentives.
              </p>
              <ul className="text-sm text-blue-100 space-y-1">
                <li>• Automated discount offers</li>
                <li>• Guest user conversion</li>
                <li>• Improved cash flow</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 bg-[#1F11ED] overflow-hidden">
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
            Ready to See It in Action?
            </h2>
            <p className="text-xl text-blue-100 mb-8">
            Start your free 30-day trial and experience the difference
            </p>
            <Link
              to="/signup"
              className="inline-flex items-center bg-white text-[#1F11ED] px-8 py-4 rounded-xl font-bold text-lg hover:bg-blue-50 hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
            >
              Start Your Free Trial
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
        </div>
      </section>
      </div>
    </>
  );
};

export default FeaturesPage;

